import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import { AppService } from '@shared/services/app';
import { UserService } from '@shared/services/user';
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import { AgentLogService } from './agent-log.service';
import type { ConsoleLogEntry } from './console-capture.service';
import { ConsoleCaptureService } from './console-capture.service';
import type { NetworkErrorEntry } from './network-capture.service';
import { NetworkCaptureService } from './network-capture.service';

const AGENT_LOG_WINDOW_MS = 10 * 60 * 1000;

export class DebugInfoUnavailableError extends Error {
  constructor() {
    super('Open a robot before exporting debug info, so the export can say which robot it is about.');
    this.name = 'DebugInfoUnavailableError';
  }
}

export class DebugInfoCancelledError extends Error {
  constructor() {
    super('Debug info export was cancelled.');
    this.name = 'DebugInfoCancelledError';
  }
}

export interface DebugInfoResult {
  agentLogAvailable: boolean;
  filename: string;
}

interface DebugInfoContext {
  timestampUtc: string;
  sessionElapsedMs: number;
  environment: { name: string; version: string };
  route: string;
  robot: { projectId: string; accountId: string; callsign: string };
  user: { id: string | undefined; email: string | undefined; isImpersonating: boolean };
  userAgent: string;
  viewport: { width: number; height: number };
  onLine: boolean;
  pageLoadUtc: string;
  datadogSessionId: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class DebugInfoService {
  constructor(
    private appService: AppService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private consoleCaptureService: ConsoleCaptureService,
    private networkCaptureService: NetworkCaptureService,
    private agentLogService: AgentLogService,
  ) {}

  /**
   * An export with no robot attached to it is not actionable, so the tool refuses rather than
   * producing a bundle nobody can triage. True only once a robot page has been opened.
   */
  canGenerate(): boolean {
    return Boolean(this.appService.projectId && this.appService.callsign);
  }

  async generate(abortSignal?: AbortSignal): Promise<DebugInfoResult> {
    if (!this.canGenerate()) {
      throw new DebugInfoUnavailableError();
    }

    // Freeze the instant at the click, before any async work, so the filename, summary, and
    // journalctl window all agree on the same moment rather than drifting to whenever each
    // async step happens to finish.
    const clickedAtUtc = new Date();
    const clickedAtElapsedMs = performance.now();

    const agentLog = await this.fetchAgentLog(clickedAtUtc);
    const consoleEntries = this.consoleCaptureService.getEntries();
    const networkEntries = this.networkCaptureService.getEntries();
    const context = this.buildContext(clickedAtUtc, clickedAtElapsedMs);
    const summary = this.buildSummary(context, consoleEntries, networkEntries, agentLog.available);

    const zip = new JSZip();
    zip.file('summary.md', summary);
    zip.file('context.json', JSON.stringify(context, null, 2));
    zip.file('console.log', this.formatConsoleEntries(consoleEntries));
    zip.file('network-errors.json', JSON.stringify(networkEntries, null, 2));
    zip.file(agentLog.available ? 'agent-journal.log' : 'agent-journal-unavailable.txt', agentLog.content);

    // generateAsync (over the sync API) so compressing ~1MB of buffers never blocks the main
    // thread and stutters the live video feed.
    const blob = await zip.generateAsync({ type: 'blob' });
    const filename = this.buildFilename(clickedAtUtc);

    if (abortSignal?.aborted) {
      throw new DebugInfoCancelledError();
    }

    FileSaver.saveAs(blob, filename);

    await this.copySummaryToClipboard(summary);

    return { agentLogAvailable: agentLog.available, filename };
  }

  private async fetchAgentLog(clickedAtUtc: Date) {
    const sinceUtc = new Date(clickedAtUtc.getTime() - AGENT_LOG_WINDOW_MS);
    return this.agentLogService.fetch(this.appService.projectId, this.appService.callsign, sinceUtc);
  }

  private buildContext(clickedAtUtc: Date, clickedAtElapsedMs: number): DebugInfoContext {
    const user = this.userService.user;

    return {
      timestampUtc: clickedAtUtc.toISOString(),
      sessionElapsedMs: Math.round(clickedAtElapsedMs),
      environment: { name: environment.name, version: environment.version },
      route: window.location.pathname,
      robot: {
        projectId: this.appService.projectId,
        accountId: this.appService.accountId,
        callsign: this.appService.callsign,
      },
      user: {
        id: user?.id,
        email: user?.email,
        isImpersonating: this.userService.isImpersonating(),
      },
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      onLine: navigator.onLine,
      pageLoadUtc: new Date(performance.timeOrigin).toISOString(),
      datadogSessionId: this.analyticsService.getSessionId(),
    };
  }

  private buildSummary(
    context: DebugInfoContext,
    consoleEntries: readonly ConsoleLogEntry[],
    networkEntries: readonly NetworkErrorEntry[],
    agentLogAvailable: boolean,
  ): string {
    const errorCount = consoleEntries.filter((entry) => entry.level === 'error').length;
    const datadogLink = this.buildDatadogSessionLink(context.datadogSessionId);

    return [
      '# Operator debug info',
      '',
      `- **Timestamp (UTC):** ${context.timestampUtc}`,
      `- **Session elapsed:** ${context.sessionElapsedMs}ms`,
      `- **Console version:** ${context.environment.version} (${context.environment.name})`,
      `- **Project:** ${context.robot.projectId ?? 'n/a'}`,
      `- **Account:** ${context.robot.accountId ?? 'n/a'}`,
      `- **Robot (callsign):** ${context.robot.callsign ?? 'n/a'}`,
      `- **Operator:** ${context.user.email ?? 'unknown'}${context.user.isImpersonating ? ' (impersonating)' : ''}`,
      `- **Datadog session:** ${datadogLink ?? 'unavailable'}`,
      '',
      '## Captured evidence',
      '',
      `- Console entries captured: ${consoleEntries.length} (${errorCount} errors)`,
      `- Failed requests captured: ${networkEntries.length}`,
      `- Agent journal: ${agentLogAvailable ? 'available' : 'unavailable, see agent-journal-unavailable.txt'}`,
      '',
    ].join('\n');
  }

  private buildDatadogSessionLink(sessionId: string | undefined): string | undefined {
    if (!sessionId) {
      return undefined;
    }

    const site = environment.datadog.site ?? 'datadoghq.com';
    return `https://app.${site}/rum/explorer?query=%40session.id%3A${sessionId}`;
  }

  private formatConsoleEntries(entries: readonly ConsoleLogEntry[]): string {
    return entries
      .map((entry) => {
        const repeatSuffix = entry.repeatCount > 1 ? ` (x${entry.repeatCount})` : '';
        return `${entry.tsUtc} [${entry.level}] ${entry.message}${repeatSuffix}`;
      })
      .join('\n');
  }

  private buildFilename(clickedAtUtc: Date): string {
    const callsign = this.appService.callsign ?? 'no-robot';
    const timestamp = clickedAtUtc.toISOString().replace(/[:.]/g, '-');
    return `debug-info-${callsign}-${timestamp}.zip`;
  }

  private async copySummaryToClipboard(summary: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(summary);
    } catch {
      // Best-effort — the zip download is the primary deliverable, clipboard access can be
      // blocked by browser permissions and must not fail the bundle.
    }
  }
}
